// Extracted verbatim from the v3 monolith (see BIRDSEYE.md module split plan).

import { DATA_FACET_COUNT } from './constants.js';
import { toArray, toInt, normalizeText, callHook, dispatchEventSafe } from './utils.js';
import { isNativeFormControl, setChipActive, syncNativeToggleState } from './controls.js';
import { setProgressState } from './ui-state.js';

export var applyFilters = function (opts) {
  var options = Object.assign(
    {
      resetPage: false,
      reason: 'manual',
      skipAutoEnsure: false,
      skipSearchEscalate: false,
    },
    opts || {}
  );

  if (options.resetPage) {
    this.currentPage = 1;
  }

  this._syncWildcardChips();

  var state = this._collectState();
  this._debugLog('filter:start', {
    reason: options.reason,
    groups: Object.keys(state.filtersByField),
    searchGroups: state.searchGroups.length,
    page: this.currentPage,
    items: this.items.length,
  });

  callHook(this.hooks.onBeforeFilter, state, this);
  dispatchEventSafe(this.wrapper, 'panda:filter:start', state);

  this.filteredItems = this._computeFilteredItems(state);

  this._render({
    reason: options.reason,
    state: state,
    filteredCount: this.filteredItems.length,
    totalCount: this.items.length,
  });

  callHook(this.hooks.onAfterFilter, state, this);
  dispatchEventSafe(this.wrapper, 'panda:filter:end', {
    filteredCount: this.filteredItems.length,
    totalCount: this.items.length,
    state: state,
  });

  if (this.urlWrite) {
    this._writeStateToUrl(state);
  }

  if (!options.skipSearchEscalate && this._shouldEscalateSearch(state)) {
    this._escalateSearchLoadAll(state);
    return;
  }

  if (this.loadMode === 'auto' && !options.skipAutoEnsure) {
    this.ensureEnoughItemsForCurrentView(state);
  }
};

export var goToPage = function (page) {
  var nextPage = toInt(page, this.currentPage);

  if (nextPage < 1) {
    nextPage = 1;
  }

  if (this.totalPages > 0 && nextPage > this.totalPages) {
    nextPage = this.totalPages;
  }

  if (nextPage === this.currentPage) {
    return;
  }

  this.currentPage = nextPage;
  this.applyFilters({ resetPage: false, reason: 'page' });
};

export var reset = function () {
  this.filterControls.forEach(function (control) {
    if (!isNativeFormControl(control)) {
      setChipActive(control, false);
      return;
    }

    var tag = control.tagName.toLowerCase();
    var type = (control.type || '').toLowerCase();

    if (type === 'checkbox' || type === 'radio') {
      control.checked = false;
      syncNativeToggleState(control);
      return;
    }

    if (tag === 'select') {
      if (control.multiple) {
        toArray(control.options).forEach(function (opt) {
          opt.selected = false;
        });
      } else {
        var wildcardOption = toArray(control.options).find(function (opt) {
          return opt.value === '*';
        });
        control.value = wildcardOption ? '*' : '';
      }
      return;
    }

    control.value = '';
  });

  this.searchControls.forEach(function (control) {
    control.value = '';
  });

  this.logicToggles.forEach(function (control) {
    var type = (control.type || '').toLowerCase();
    if (type === 'checkbox') {
      control.checked = false;
    } else if (type === 'radio') {
      control.checked = normalizeText(control.value) === 'and';
    } else {
      control.value = 'and';
    }
  });

  this.currentLogic = this.defaultLogic;
  this.currentPage = 1;
  this.applyFilters({ resetPage: false, reason: 'reset' });
};

// The default dom render mode detaches hidden items from the list. Anything that
// forgets tracked records (refresh/destroy) must re-mount them first, otherwise
// every re-init permanently loses all items except the currently visible page.
export var _remountAllItems = function () {
  if (!this.list) {
    return;
  }

  var list = this.list;
  this.items.forEach(function (record) {
    var el = record.el;
    el.style.display = '';
    el.hidden = false;
    el.removeAttribute('aria-hidden');
    if (!list.contains(el)) {
      list.appendChild(el);
    }
  });
};

export var refresh = function (opts) {
  var options = Object.assign(
    {
      resetPage: false,
    },
    opts || {}
  );

  this._muteMutations();
  this._remountAllItems();
  this._unmuteMutationsAsync();

  this.items.forEach(function (record) {
    this._stopElementAnimation(record.el);
    delete record.el.__pfRecordId;
    delete record.el.__pfOwnerUid;
  }, this);

  this.items = [];
  this.itemKeySet = new Set();
  this.itemIdSeq = 0;

  this.facetCountNodes = toArray(this.wrapper.querySelectorAll(DATA_FACET_COUNT));
  this._collectFacetCountDefs();

  this._collectInitialItems();

  this.applyFilters({
    resetPage: options.resetPage,
    reason: 'refresh',
  });
};

export var destroy = function () {
  setProgressState(this, false);

  if (this._numberClickCleanup) {
    this._numberClickCleanup();
    this._numberClickCleanup = null;
  }

  if (this._mutationObserver) {
    this._mutationObserver.disconnect();
    this._mutationObserver = null;
  }

  if (this.infiniteObserver) {
    this.infiniteObserver.disconnect();
    this.infiniteObserver = null;
  }

  if (this._ownsInfiniteSentinel && this.infiniteSentinel && this.infiniteSentinel.parentNode) {
    this.infiniteSentinel.parentNode.removeChild(this.infiniteSentinel);
  }
  this.infiniteSentinel = null;
  this._ownsInfiniteSentinel = false;

  while (this._listeners.length) {
    var cleanup = this._listeners.pop();
    if (typeof cleanup === 'function') {
      cleanup();
    }
  }

  // Mutation observer is already disconnected above, so this cannot re-trigger it.
  this._remountAllItems();

  this.items.forEach(function (record) {
    this._stopElementAnimation(record.el);
    delete record.el.__pfRecordId;
    delete record.el.__pfOwnerUid;
  }, this);

  this.wrapper.__pandaFilterInstance = null;
};

